import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Switch,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import axios from 'axios';
import { API_URL, CURRENCY } from 'constants';

export default function PointsConversion() {
  const [config, setConfig] = useState({
    enabled: false,
    conversionRate: '',
    allowProfilesToSeeMoney: false,
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/points-conversion`, {
        headers: {
          'x-access-token': token,
        },
      });
      setConfig({
        enabled: response.data.enabled || false,
        conversionRate: response.data.conversionRate || '',
        allowProfilesToSeeMoney: response.data.allowProfilesToSeeMoney || false,
      });
    } catch (error) {
      setErrorMessage('Erreur lors de la récupération de la configuration');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleSave = async () => {
    setSuccessMessage('');
    setErrorMessage('');
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const rate = Number(config.conversionRate);
      if (config.enabled && (isNaN(rate) || rate < 0)) {
         throw new Error("Le taux de conversion doit être un nombre supérieur ou égal à 0.");
      }

      await axios.put(
        `${API_URL}/api/user/points-conversion`,
        {
          enabled: config.enabled,
          conversionRate: isNaN(rate) ? null : rate,
          allowProfilesToSeeMoney: config.allowProfilesToSeeMoney,
        },
        {
          headers: {
            'x-access-token': token,
          },
        }
      );
      setSuccessMessage('Configuration enregistrée');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.message || 'Erreur lors de la sauvegarde de la configuration');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={4}>
      <Box p={6} bg="white" borderRadius="xl" boxShadow="lg" maxW="600px" mx="auto">
        <Heading size="md" mb={6}>Paramètres de conversion des points</Heading>
        
        {successMessage && (
          <Alert status="success" mb={4} borderRadius="md">
            <AlertIcon />
            {successMessage}
          </Alert>
        )}
        
        {errorMessage && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            {errorMessage}
          </Alert>
        )}

        <VStack spacing={6} align="stretch">
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="enabled" mb="0">
              Activer la conversion des points en argent
            </FormLabel>
            <Switch
              id="enabled"
              isChecked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              colorScheme="brand"
            />
          </FormControl>

          {config.enabled && (
            <>
              <FormControl>
                <FormLabel>Taux (ex: tapez "2" pour que 1 point = 2 {CURRENCY.code})</FormLabel>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={config.conversionRate}
                  onChange={(e) => setConfig({ ...config, conversionRate: e.target.value })}
                  placeholder="Entrez le taux de conversion"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="allow-profiles" mb="0">
                  Autoriser les profils à voir l'équivalent en argent
                </FormLabel>
                <Switch
                  id="allow-profiles"
                  isChecked={config.allowProfilesToSeeMoney}
                  onChange={(e) => setConfig({ ...config, allowProfilesToSeeMoney: e.target.checked })}
                  colorScheme="brand"
                />
              </FormControl>
              <Text fontSize="sm" color="gray.500">
                Si activé, les profils auront un bouton dans leur application pour voir les points convertis en argent.
              </Text>
            </>
          )}

          <Button
            colorScheme="brand"
            bg="#0EB182"
            color="white"
            onClick={handleSave}
            isLoading={loading}
            _hover={{ bg: "#0c956e" }}
          >
            Enregistrer la configuration
          </Button>
        </VStack>
      </Box>
    </Box>
  );
}